import React, { useState, useEffect } from 'react';
import { User, Bell, Shield, ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Database } from '../lib/database.types';
import { cn } from '../lib/utils';

export default function UserSettings() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [user, setUser] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Get current user
  useEffect(() => {
    const getUser = async () => {
      const { data, error } = await supabase.auth.getUser();
      if (data.user) {
        setUser(data.user);
      }
    };
    
    getUser();
  }, [supabase]);
  
  // Parse tab from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && ['profile', 'notifications', 'security'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [location]);

  // Profile settings — initialized empty, populated by the effect below once
  // `user` arrives. The previous implementation read `user?.user_metadata?.X`
  // at component-creation time when `user` was always null, so the form
  // never reflected saved values.
  const [profileForm, setProfileForm] = useState({
    fullName: '',
    company: '',
    jobTitle: '',
    phone: ''
  });

  useEffect(() => {
    if (user) {
      setProfileForm({
        fullName: user.user_metadata?.full_name || '',
        company: user.user_metadata?.company || '',
        jobTitle: user.user_metadata?.job_title || '',
        phone: user.user_metadata?.phone || ''
      });
      const savedPrefs = user.user_metadata?.notification_preferences;
      if (savedPrefs) {
        setNotificationSettings(prev => ({ ...prev, ...savedPrefs }));
      }
    }
  }, [user]);

  // True for users who signed in via OAuth (e.g., Google) and never set a
  // password. They have nothing to verify against, so we hide the password
  // form rather than show a misleading "incorrect password" error.
  const isOAuthOnly = !!user?.app_metadata?.provider && user.app_metadata.provider !== 'email';

  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    marketUpdates: true,
    newFeatures: true,
    propertyAlerts: false
  });

  // Security settings
  const [securityForm, setSecurityForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({ ...prev, [name]: value }));
  };

  const handleNotificationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setNotificationSettings(prev => ({ ...prev, [name]: checked }));
  };

  const handleSecurityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSecurityForm(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          full_name: profileForm.fullName,
          company: profileForm.company,
          job_title: profileForm.jobTitle,
          phone: profileForm.phone
        }
      });
      
      if (error) throw error;
      
      setSuccessMessage('Profile updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateNotifications = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      // Store notification preferences in user metadata
      const { error } = await supabase.auth.updateUser({
        data: {
          notification_preferences: notificationSettings
        }
      });
      
      if (error) throw error;
      
      setSuccessMessage('Notification preferences updated');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating notifications:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!securityForm.currentPassword) {
      alert('Please enter your current password');
      return;
    }

    if (securityForm.newPassword !== securityForm.confirmPassword) {
      alert("Passwords don't match");
      return;
    }

    if (securityForm.newPassword.length < 6) {
      alert('New password must be at least 6 characters');
      return;
    }

    if (!user?.email) {
      alert('No email associated with this account');
      return;
    }

    setIsSaving(true);

    try {
      // Verify the current password by attempting to sign in with it. Supabase
      // doesn't expose a "verify password" endpoint, so re-authenticating is
      // the canonical pattern. A successful sign-in proves the password is
      // correct without changing the session (the same user just gets a
      // refreshed token).
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: securityForm.currentPassword,
      });

      if (verifyError) {
        alert('Current password is incorrect');
        setIsSaving(false);
        return;
      }

      // Current password verified — apply the new one.
      const { error } = await supabase.auth.updateUser({
        password: securityForm.newPassword
      });

      if (error) throw error;

      setSuccessMessage('Password updated successfully');
      setSecurityForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating password:', error);
      alert('Failed to update password. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold">Account Settings</h1>
      </div>

      {successMessage && (
        <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 mb-4 rounded">
          {successMessage}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="w-full">
          <div className="bg-gray-50 border-b">
            <div className="flex">
              <button
                onClick={() => {
                  setActiveTab('profile');
                  navigate('/user-settings?tab=profile', { replace: true });
                }}
                className={cn(
                  "flex items-center px-6 py-4 border-b-2 transition-colors",
                  activeTab === 'profile' 
                    ? "border-blue-600 text-blue-600" 
                    : "border-transparent text-gray-500 hover:text-gray-700"
                )}
              >
                <User className="w-4 h-4 mr-2" />
                Profile
              </button>
              <button
                onClick={() => {
                  setActiveTab('notifications');
                  navigate('/user-settings?tab=notifications', { replace: true });
                }}
                className={cn(
                  "flex items-center px-6 py-4 border-b-2 transition-colors",
                  activeTab === 'notifications' 
                    ? "border-blue-600 text-blue-600" 
                    : "border-transparent text-gray-500 hover:text-gray-700"
                )}
              >
                <Bell className="w-4 h-4 mr-2" />
                Notifications
              </button>
              <button
                onClick={() => {
                  setActiveTab('security');
                  navigate('/user-settings?tab=security', { replace: true });
                }}
                className={cn(
                  "flex items-center px-6 py-4 border-b-2 transition-colors",
                  activeTab === 'security' 
                    ? "border-blue-600 text-blue-600" 
                    : "border-transparent text-gray-500 hover:text-gray-700"
                )}
              >
                <Shield className="w-4 h-4 mr-2" />
                Security
              </button>
            </div>
          </div>

          {/* Profile Settings */}
          {activeTab === 'profile' && (
            <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Profile Information</h2>
            <form onSubmit={handleUpdateProfile}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={profileForm.fullName}
                    onChange={handleProfileChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your full name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={profileForm.company}
                    onChange={handleProfileChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your company name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Title
                  </label>
                  <input
                    type="text"
                    name="jobTitle"
                    value={profileForm.jobTitle}
                    onChange={handleProfileChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your job title"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={profileForm.phone}
                    onChange={handleProfileChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your phone number"
                  />
                </div>
                
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-300"
                  >
                    {isSaving ? 'Saving...' : 'Update Profile'}
                  </button>
                </div>
              </div>
            </form>
          </div>
          )}

          {/* Notification Settings */}
          {activeTab === 'notifications' && (
            <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Notification Preferences</h2>
            <form onSubmit={handleUpdateNotifications}>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="emailNotifications"
                    name="emailNotifications"
                    checked={notificationSettings.emailNotifications}
                    onChange={handleNotificationChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="emailNotifications" className="ml-2 block text-sm text-gray-900">
                    Email Notifications
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="marketUpdates"
                    name="marketUpdates"
                    checked={notificationSettings.marketUpdates}
                    onChange={handleNotificationChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="marketUpdates" className="ml-2 block text-sm text-gray-900">
                    Market Updates
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="newFeatures"
                    name="newFeatures"
                    checked={notificationSettings.newFeatures}
                    onChange={handleNotificationChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="newFeatures" className="ml-2 block text-sm text-gray-900">
                    New Features & Updates
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="propertyAlerts"
                    name="propertyAlerts"
                    checked={notificationSettings.propertyAlerts}
                    onChange={handleNotificationChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="propertyAlerts" className="ml-2 block text-sm text-gray-900">
                    Property Alerts
                  </label>
                </div>
                
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-300"
                  >
                    {isSaving ? 'Saving...' : 'Update Preferences'}
                  </button>
                </div>
              </div>
            </form>
          </div>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Security Settings</h2>
            {isOAuthOnly ? (
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                <p className="text-sm text-blue-900">
                  You signed in with <span className="font-semibold capitalize">{user?.app_metadata?.provider}</span>.
                  Password changes aren't available because your account is managed by your single sign-on provider.
                  To change your password, update it through your SSO provider's account settings.
                </p>
              </div>
            ) : (
            <form onSubmit={handleUpdatePassword}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={securityForm.currentPassword}
                    onChange={handleSecurityChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter current password"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    value={securityForm.newPassword}
                    onChange={handleSecurityChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter new password"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={securityForm.confirmPassword}
                    onChange={handleSecurityChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Confirm new password"
                  />
                </div>
                
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSaving || !securityForm.newPassword || securityForm.newPassword !== securityForm.confirmPassword}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-300"
                  >
                    {isSaving ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </div>
            </form>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
